import { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message } from "antd";
import {
  fetchAllMarketsAction,
  fetchSKUCompaniesAction,
  fetchCompanyAllBrandsAction,
  appendGeoscopeMarketsAction,
  clearMarkets,
  clearSKUCompanies,
  clearCompanyBrands,
} from "../../../../slices/internalToolSlice";
import {
  clearCompetitors,
  fetchCompaniesWithBrandsData,
} from "../../../../slices/competitorsSlice";
import { updatePetFormulaAction } from "../../../../slices/workspaceSlice";
import {
  removeGeoscopeMarkets,
  deleteGeoscopeRegion,
} from "../../../../api/internal_tool_endpoints";
import { useMarketConfig } from "../../hooks/useMarketConfig";
import { prefillFromRow } from "../../../../utils/petFormulaPrefill";

// Stable region key — the Add Market flow configures exactly one new market, so
// a single fixed region id keeps the `regionId|market` composite keys (used by
// the shared market-config helpers) stable across re-renders.
const REGION_ID = "add_market_region";

// What the backend reports for a formula country that no geoscope region
// claims. It marks a gap, so it must never be offered as a region to add to.
const UNKNOWN_REGION = "Unknown Region";

/**
 * Form state + submit logic for adding a single new market (country) to an
 * existing workspace. Owns Step1 (workspace picker, region name, single market
 * select) and the submit flow; delegates the weights/assignments middle to the
 * shared `useMarketConfig` hook (the same one the Create Workspace wizard uses).
 */
export const useAddMarketForm = (open, onSuccess) => {
  const dispatch = useDispatch();
  const { markets, marketsLoading } = useSelector(
    (state) => state.internalTool
  );
  const { allWorkspaces, addMarketLoading } = useSelector(
    (state) => state.workspace
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  // Region is either picked from the workspace's existing regions or typed as
  // a brand-new one. Real add-market work appends to an existing region, so
  // that is the default mode whenever the workspace has any.
  const [regionMode, setRegionMode] = useState("existing");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [regionName, setRegionName] = useState("");
  const [newMarket, setNewMarket] = useState("");
  const [copyFromMarket, setCopyFromMarket] = useState("");
  const [contractMonthRange, setContractMonthRange] = useState([null, null]);

  // One row per workspace, deduped by id_client (allWorkspaces has one row per
  // workspace/region/market). Each entry keeps the client (mapped_id_client),
  // the markets already configured so the new-market picker can exclude them,
  // the workspace's existing region names, and each market's backend row so a
  // new market's config can be prefilled from a sibling market. All of this is
  // already in `allWorkspaces` — none of it needs another fetch.
  const workspaces = useMemo(() => {
    const byId = {};
    (allWorkspaces || []).forEach((ws) => {
      const id = ws.id_client;
      if (!id) return;
      if (!byId[id]) {
        byId[id] = {
          id_client: id,
          mapped_id_client: ws.mapped_id_client || "",
          markets: new Set(),
          regions: new Set(),
          rowsByMarket: {},
        };
      }
      const entry = byId[id];
      if (!entry.mapped_id_client && ws.mapped_id_client) {
        entry.mapped_id_client = ws.mapped_id_client;
      }
      if (ws.region && ws.region !== UNKNOWN_REGION) {
        entry.regions.add(ws.region);
      }
      const rowMarkets = Array.isArray(ws.markets)
        ? ws.markets
        : typeof ws.markets === "string"
          ? ws.markets.split(",").map((m) => m.trim())
          : [];
      rowMarkets.filter(Boolean).forEach((m) => {
        const code = String(m).toUpperCase();
        entry.markets.add(code);
        // One row per workspace x country, so the row IS that market's config.
        entry.rowsByMarket[code] = ws;
      });
    });
    return Object.values(byId).map((w) => ({
      ...w,
      markets: Array.from(w.markets),
      regions: Array.from(w.regions).sort((a, b) => a.localeCompare(b)),
    }));
  }, [allWorkspaces]);

  const workspaceOptions = useMemo(
    () =>
      [...workspaces]
        .sort((a, b) => a.id_client.localeCompare(b.id_client))
        .map((w) => ({ value: w.id_client, label: w.id_client })),
    [workspaces]
  );

  const selectedWorkspaceObj = useMemo(
    () => workspaces.find((w) => w.id_client === selectedWorkspace) || null,
    [workspaces, selectedWorkspace]
  );

  // The client identifier used for market/company/brand lookups.
  const clientId = selectedWorkspaceObj?.mapped_id_client || "";

  const existingMarkets = useMemo(
    () => selectedWorkspaceObj?.markets || [],
    [selectedWorkspaceObj]
  );

  const allClientMarkets = useMemo(
    () => (Array.isArray(markets) ? markets : []),
    [markets]
  );

  // The workspace's existing regions, as pick-from options.
  const regionOptions = useMemo(
    () =>
      (selectedWorkspaceObj?.regions || []).map((r) => ({
        value: r,
        label: r,
      })),
    [selectedWorkspaceObj]
  );

  // Markets the workspace doesn't already have — the only valid new-market
  // choices.
  const availableMarketOptions = useMemo(() => {
    const existing = new Set(existingMarkets.map((m) => m.toUpperCase()));
    return allClientMarkets
      .filter((m) => !existing.has(String(m).toUpperCase()))
      .map((m) => ({ value: m, label: String(m).toUpperCase() }));
  }, [allClientMarkets, existingMarkets]);

  // Markets whose config can seed the new one.
  const copyFromMarketOptions = useMemo(
    () => existingMarkets.map((m) => ({ value: m, label: m })),
    [existingMarkets]
  );

  // The region this submit targets, whichever way it was chosen.
  const effectiveRegionName =
    regionMode === "existing" ? selectedRegion : (regionName || "").trim();

  // The country code both writes use. pet_formula labels a country by exact
  // membership in a geoscope region's markets, so the geoscope write and the
  // formula write must agree on case or the row renders as "Unknown Region"
  // even when both succeed. Uppercase is what every other writer stores.
  const marketCode = useMemo(
    () => String(newMarket || "").trim().toUpperCase(),
    [newMarket]
  );

  // Derived single-region/single-market structure so the shared market-config
  // helpers' getPairs contract holds.
  const customRegions = useMemo(() => {
    if (!effectiveRegionName || !newMarket) return {};
    return {
      [REGION_ID]: { name: effectiveRegionName, markets: [newMarket] },
    };
  }, [effectiveRegionName, newMarket]);

  const marketConfig = useMarketConfig({ clientId, customRegions });
  const {
    calcTotal,
    marketAssignments,
    buildMarketConfig,
    resetMarketConfig,
    setMarketWeights,
    setMarketAssignments,
  } = marketConfig;

  const marketOptions = useMemo(
    () =>
      allClientMarkets.map((m) => ({
        value: m,
        label: String(m).toUpperCase(),
      })),
    [allClientMarkets]
  );

  const handleWorkspaceChange = useCallback(
    (val) => {
      setSelectedWorkspace(val || "");
      setNewMarket("");
      setCopyFromMarket("");
      setSelectedRegion("");
      setRegionName("");
      resetMarketConfig();

      const ws = workspaces.find((w) => w.id_client === val);
      // A workspace with no regions yet can only get a new one.
      setRegionMode(ws?.regions?.length ? "existing" : "new");
      const client = ws?.mapped_id_client;
      if (client) {
        dispatch(fetchAllMarketsAction({ id_client: client }));
      } else {
        dispatch(clearMarkets());
        dispatch(clearSKUCompanies());
        dispatch(clearCompanyBrands());
      }
    },
    [dispatch, workspaces, resetMarketConfig]
  );

  const handleRegionModeChange = useCallback((mode) => {
    setRegionMode(mode);
    setSelectedRegion("");
    setRegionName("");
  }, []);

  // Picking a real region also leaves "create new" mode, so the two controls
  // can never disagree about which region the submit targets.
  const handleSelectedRegionChange = useCallback((val) => {
    setRegionMode("existing");
    setRegionName("");
    setSelectedRegion(val || "");
  }, []);

  const handleRegionNameChange = useCallback((e) => {
    setRegionName(e.target.value);
  }, []);

  const handleNewMarketChange = useCallback(
    (val) => {
      setNewMarket(val || "");
      // Changing the market orphans any weights/assignments under the old key;
      // reset them, and drop the prefill source so the copied values can't be
      // mistaken for config belonging to the newly chosen market.
      setCopyFromMarket("");
      resetMarketConfig();
    },
    [resetMarketConfig]
  );

  // Seed the new market's weights + assignments from one of the workspace's
  // existing markets. An editable starting point, not a commitment: everything
  // stays editable in Step2/Step3.
  const handleCopyFromMarketChange = useCallback(
    (val) => {
      setCopyFromMarket(val || "");
      if (!val) return;
      const row = selectedWorkspaceObj?.rowsByMarket?.[val];
      if (!row) return;
      const { weights, assignment } = prefillFromRow(row);
      const ck = `${REGION_ID}|${newMarket}`;
      setMarketWeights((p) => ({ ...p, [ck]: weights }));
      setMarketAssignments((p) => ({ ...p, [ck]: assignment }));
      // Warm the brand/competitor trees for the NEW market's country so Step3
      // can render the copied selections rather than empty pickers.
      if (assignment.company && clientId && newMarket) {
        dispatch(
          fetchCompanyAllBrandsAction({
            id_client: clientId,
            country: newMarket,
            company: assignment.company,
          })
        );
        dispatch(
          fetchCompaniesWithBrandsData({
            id_client: clientId,
            country: newMarket,
          })
        ).catch((error) => {
          console.error("fetchCompaniesWithBrandsData error:", error);
        });
      }
    },
    [
      dispatch,
      selectedWorkspaceObj,
      newMarket,
      clientId,
      setMarketWeights,
      setMarketAssignments,
    ]
  );

  const handleContractMonthRangeChange = useCallback((dates) => {
    setContractMonthRange(dates || [null, null]);
  }, []);

  // Reset everything when the modal closes.
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedWorkspace("");
      setRegionMode("existing");
      setSelectedRegion("");
      setRegionName("");
      setNewMarket("");
      setCopyFromMarket("");
      setContractMonthRange([null, null]);
      resetMarketConfig();
      dispatch(clearMarkets());
      dispatch(clearCompetitors());
    }
  }, [open, dispatch, resetMarketConfig]);

  // Fetch SKU companies for the new market when reaching the assignments step.
  useEffect(() => {
    if (currentStep === 2 && clientId && open && newMarket) {
      dispatch(
        fetchSKUCompaniesAction({ id_client: clientId, country: newMarket })
      );
    }
  }, [currentStep, clientId, open, newMarket, dispatch]);

  // Build the single-country payload for PUT /{id_client}/pet_formula/update.
  const buildCountryPayload = useCallback(() => {
    const ck = `${REGION_ID}|${newMarket}`;
    const country = {
      country: marketCode,
      ...buildMarketConfig(ck),
    };
    if (contractMonthRange?.[0] && contractMonthRange?.[1]) {
      country.start_end_month = [
        contractMonthRange[0].format("YYYY-MM"),
        contractMonthRange[1].format("YYYY-MM"),
      ];
    }
    return country;
  }, [newMarket, marketCode, buildMarketConfig, contractMonthRange]);

  const prev = () => setCurrentStep((s) => Math.max(0, s - 1));

  // Undo the geoscope append after a failed pet_formula write, so a failed
  // submit leaves the workspace exactly as it was. Drops the whole region when
  // this submit created it, otherwise takes back only the codes it added.
  const rollbackGeoscope = async (workspaceIdentifier, appended) => {
    if (!appended) return true;
    try {
      if (appended.created) {
        await deleteGeoscopeRegion({
          id_client: workspaceIdentifier,
          region: appended.region_key,
        });
      } else if (appended.added_markets?.length) {
        await removeGeoscopeMarkets({
          id_client: workspaceIdentifier,
          region: appended.region_key,
          country_codes: appended.added_markets,
        });
      }
      return true;
    } catch (rollbackError) {
      console.error("❌ Failed to roll back geoscope:", rollbackError);
      return false;
    }
  };

  const finish = async () => {
    const workspaceIdentifier = selectedWorkspace.trim();
    const region = effectiveRegionName;
    let appended = null;

    try {
      // 1) Map the new market into a region. This goes first because it is the
      //    reversible half: step 2 schedules a pipeline run that cannot be
      //    undone, so it must be the last thing that happens.
      const appendResult = await dispatch(
        appendGeoscopeMarketsAction({
          id_client: workspaceIdentifier,
          regions_info: {
            regions: [{ region, country_codes: [marketCode] }],
          },
        })
      ).unwrap();
      appended = appendResult?.regions?.[0] || null;

      // 2) Add the new market to the workspace's pet_formula. Creates the new
      //    master version and triggers the pipeline.
      const result = await dispatch(
        updatePetFormulaAction({
          id_client: workspaceIdentifier,
          formula_update: {
            countries: [buildCountryPayload()],
            comment: null,
            default_id_client: clientId,
          },
        })
      ).unwrap();

      message.success(
        `Market ${marketCode} added to ${workspaceIdentifier} successfully!`
      );
      onSuccess?.(result);
    } catch (error) {
      console.error("❌ Failed to add market:", error);
      const detail = error?.message || String(error);

      // A status means the server answered and rejected the write, so nothing
      // was applied and the earlier geoscope append is safe to undo. Without
      // one (timeout, dropped connection) the outcome is unknown: undoing
      // could strip the region mapping from a market whose version was in fact
      // created, so leave it alone and tell the operator to check.
      if (appended && typeof error?.status !== "number") {
        message.error(
          `Add market did not complete for ${marketCode}: ${detail}. ` +
            `The result is unknown, so nothing was rolled back — check ` +
            `${workspaceIdentifier} in the table before retrying.`,
          12
        );
        return;
      }

      if (appended) {
        const undone = await rollbackGeoscope(workspaceIdentifier, appended);
        message.error(
          undone
            ? `Failed to add market: ${detail}. No changes were saved.`
            : `Failed to add market: ${detail}. Rolling back the region ` +
                `change also failed, so ${marketCode} may still be listed ` +
                `under ${appended.region_name} — remove it before retrying.`,
          undone ? 8 : 12
        );
        return;
      }

      message.error(`Failed to add market: ${detail}`);
    }
  };

  const validateStep = (step) => {
    if (step === 0) {
      if (!selectedWorkspace) {
        message.error("Please select a workspace");
        return false;
      }
      if (!effectiveRegionName) {
        message.error(
          regionMode === "existing"
            ? "Please select a region"
            : "Please enter a region name"
        );
        return false;
      }
      if (!newMarket) {
        message.error("Please select a market to add");
        return false;
      }
      if (contractMonthRange?.[0] && contractMonthRange?.[1]) {
        if (contractMonthRange[0].isAfter(contractMonthRange[1])) {
          message.error("Contract start month must be before end month");
          return false;
        }
      }
      return true;
    }
    if (step === 1) {
      const ck = `${REGION_ID}|${newMarket}`;
      if (Math.abs(calcTotal(ck) - 100) > 0.1) {
        message.error("Market KPI weights must total 100%");
        return false;
      }
      return true;
    }
    if (step === 2) {
      const ck = `${REGION_ID}|${newMarket}`;
      const assignment = marketAssignments[ck];
      if (!assignment?.company) {
        message.error("Please select a company for the market");
        return false;
      }
      if (!assignment?.coreBrandGroups?.length) {
        message.error("Please add at least one core brand group");
        return false;
      }
      return true;
    }
    return true;
  };

  const next = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < 3) {
      setCurrentStep((s) => s + 1);
    } else {
      finish();
    }
  };

  return {
    // shared market-config surface (Step2 + Step3)
    ...marketConfig,
    // Step1 (Add Market specific)
    selectedWorkspace,
    workspaceOptions,
    handleWorkspaceChange,
    regionMode,
    handleRegionModeChange,
    selectedRegion,
    handleSelectedRegionChange,
    regionOptions,
    regionName,
    handleRegionNameChange,
    newMarket,
    handleNewMarketChange,
    availableMarketOptions,
    existingMarkets,
    copyFromMarket,
    handleCopyFromMarketChange,
    copyFromMarketOptions,
    // shared with reused steps / Step4
    currentStep,
    clientId,
    workspaceName: selectedWorkspace,
    client: clientId ? { name: clientId } : undefined,
    customRegions,
    contractMonthRange,
    allClientMarkets,
    marketOptions,
    handleContractMonthRangeChange,
    markets,
    marketsLoading,
    prev,
    next,
    finish,
    // Submit state (mirrors useWorkspaceForm's workspaceLoading name)
    workspaceLoading: addMarketLoading,
  };
};
